/**
 * Centralized Constants for PromptShelf Frontend
 * PBI 631: Implement centralized constants for API endpoints and storage keys
 */

// API Configuration
const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_URL || 'https://api-dev.promptshelf.ai';
};

// SSO Configuration
const getGoogleClientId = (): string => {
  return import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
};

export const API_ENDPOINTS = {
  // Base URL
  BASE_URL: getApiBaseUrl(),
  
  // Authentication endpoints
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    CHECK_SESSION: '/api/auth/me',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    VERIFY_RESET_OTP: '/api/auth/verify-reset-otp',
    RESEND_RESET_OTP: '/api/auth/resend-reset-otp',
    RESET_PASSWORD_CONFIRM: '/api/auth/reset-password-confirm',
    // Legacy OTP routes (for email verification)
    VERIFY_OTP: '/api/auth/verify-otp',
    RESEND_OTP: '/api/auth/resend-otp',
    RESET_PASSWORD: '/api/auth/reset-password',
    VERIFY_EMAIL: '/api/auth/verify-email',
    RESEND_VERIFICATION: '/api/auth/resend-verification',
    SEND_NEW_EMAIL_VERIFICATION: '/api/auth/send-new-email-verification',
    VERIFY_NEW_EMAIL: '/api/auth/verify-new-email',
    SSO: {
      GOOGLE: '/api/sso/google',
      GOOGLE_CALLBACK: '/api/sso/google/callback',
      MICROSOFT: '/api/sso/microsoft',
      MICROSOFT_CALLBACK: '/api/sso/microsoft/callback',
      CONFIG: '/api/sso/config',
    },
  },
  
  // Prompts endpoints
  PROMPTS: {
    LIST: '/api/prompts',
    CREATE: '/api/prompts',
    SEARCH: '/api/prompts/search',
    CATEGORY: (categoryName: string) => `/api/prompts/category/${categoryName}`,
    DETAIL: (id: string) => `/api/prompts/${id}`,
    VOTE: (id: string) => `/api/prompts/${id}/vote`,
    TOGGLE_VISIBILITY: (id: number) => `/api/prompts/${id}/toggle-public`,
    VERSIONS: {
      LIST: (promptId: string) => `/api/prompts/${promptId}/versions`,
      CREATE: (promptId: string) => `/api/prompts/${promptId}/versions`,
    },
  },

  // Analytics endpoints
  ANALYTICS: {
    PROMPT_EVENT: '/api/analytics/prompt-event',
    PROMPT_EVENT_DETAIL: (promptId: number) => `/api/analytics/prompt-event/${promptId}`,
  },
  
  // Categories endpoints
  CATEGORIES: {
    CREATE: '/api/categories',
    LIST: '/api/categories',
    WITH_COUNT: '/api/categories/with-count',
  },
  
  // Collections endpoints
  COLLECTIONS: {
    LIST: '/api/collections',
    CREATE: '/api/collections',
    DETAIL: (id: string) => `/api/collections/${id}`,
    UPDATE: (id: string) => `/api/collections/${id}`,
    DELETE: (id: string) => `/api/collections/${id}`,
    ADD_PROMPT: (id: string) => `/api/collections/${id}/add-prompt`,
    REMOVE_PROMPT: (id: string, promptId: string) => `/api/collections/${id}/remove-prompt/${promptId}`,
    PROMPTS: (id: string) => `/api/collections/${id}/prompts`,
  },
  
  // Waitlist endpoints
  WAITLIST: {
    CREATE: '/api/waitlist',
  },
  
  // User profile endpoints
  USER_PROFILE: {
    UPDATE: '/api/user-profile',
  },
  
  // Email update endpoints
  EMAIL_UPDATE: {
    SEND_VERIFICATION: '/api/auth/send-email-update-verification',
    VERIFY: '/api/auth/verify-email-update',
  },
  
  // Subscription and billing endpoints
  SUBSCRIPTION: {
    GET_USER_SUBSCRIPTION: '/api/subscriptions/current',
    GET_USER_INVOICES: '/api/subscriptions/invoices',
    MANAGE: '/api/subscriptions/manage',
    CANCEL: '/api/subscriptions/cancel',
    GET_PLANS: '/api/subscriptions/plans',
    SYNC_CHECKOUT_SESSION: '/api/subscriptions/sync-checkout-session', // ✅ ROBUST FIX: Sync with deduplication
    CHANGE_PLAN: '/api/subscriptions/change-plan', // ✅ PBI 11.2.11: Proration
    GET_SCHEDULED_CHANGE: '/api/subscriptions/scheduled-change',
    SCHEDULE_DOWNGRADE: '/api/subscriptions/schedule-downgrade',
    CANCEL_SCHEDULED_DOWNGRADE: '/api/subscriptions/cancel-scheduled-downgrade',
  },
  
  // Discount endpoints
  DISCOUNTS: {
    VALIDATE: '/api/discounts/validate',
    APPLY_TO_SUBSCRIPTION: '/api/discounts/apply-to-subscription',
    USER_REDEMPTIONS: '/api/discounts/user-redemptions',
    CURRENT: '/api/discounts/current',
  },
  
  // Stripe endpoints
  STRIPE: {
    CREATE_CHECKOUT_SESSION: '/api/stripe/create-checkout-session',
    CREATE_PORTAL_SESSION: '/api/stripe/create-portal-session',
    GET_PRICES: '/api/stripe/prices',
  },
  
  // Billing endpoints (legacy - keeping for compatibility)
  BILLING: {
    INFO: '/api/billing/info',
    INVOICES: '/api/billing/invoices',
    PAYMENT_METHODS: '/api/billing/payment-methods',
    UPGRADE: '/api/billing/upgrade',
    CANCEL: '/api/billing/cancel',
    ADD_PAYMENT_METHOD: '/api/billing/payment-methods',
    UPDATE_PAYMENT_METHOD: (id: string) => `/api/billing/payment-methods/${id}`,
    DELETE_PAYMENT_METHOD: (id: string) => `/api/billing/payment-methods/${id}`,
  },
  
  // Agent endpoints
  AGENT: {
    ENHANCE: '/api/agent/enhance',
    HEALTH: '/api/agent/health',
  },

  // Models endpoints
  MODELS: {
    LIST: '/api/models',
  },

  // Usage endpoints
  USAGE: {
    LIMITS: '/api/v1/usage/me',
  },

  COMMENTS: (promptId: number) => `/api/prompts/${promptId}/comments`,
  VOTE_COMMENT: (commentId: number) => `/api/comments/${commentId}/vote`,
} as const;

// LocalStorage Keys - only keeping USER_DATA for potential non-auth use cases
export const STORAGE_KEYS = {
  USER_DATA: 'user', // Kept for potential future use, but not used for auth
  NEW_USER_REGISTRATION: 'new_user_registration', // Track new user registration flow
} as const;

// SSO Configuration
export const SSO_CONFIG = {
  GOOGLE_CLIENT_ID: getGoogleClientId(),
} as const;

// Helper functions for building full URLs
export const buildApiUrl = (endpoint: string): string => {
  return `${API_ENDPOINTS.BASE_URL}${endpoint}`;
};

// Pre-built full URLs for common use cases
export const API_URLS = {
  AUTH: {
    LOGIN: buildApiUrl(API_ENDPOINTS.AUTH.LOGIN),
    REGISTER: buildApiUrl(API_ENDPOINTS.AUTH.REGISTER),
    LOGOUT: buildApiUrl(API_ENDPOINTS.AUTH.LOGOUT),
    CHECK_SESSION: buildApiUrl(API_ENDPOINTS.AUTH.CHECK_SESSION),
    FORGOT_PASSWORD: buildApiUrl(API_ENDPOINTS.AUTH.FORGOT_PASSWORD),
    VERIFY_RESET_OTP: buildApiUrl(API_ENDPOINTS.AUTH.VERIFY_RESET_OTP),
    RESEND_RESET_OTP: buildApiUrl(API_ENDPOINTS.AUTH.RESEND_RESET_OTP),
    RESET_PASSWORD_CONFIRM: buildApiUrl(API_ENDPOINTS.AUTH.RESET_PASSWORD_CONFIRM),
    // Legacy OTP routes (for email verification)
    VERIFY_OTP: buildApiUrl(API_ENDPOINTS.AUTH.VERIFY_OTP),
    RESEND_OTP: buildApiUrl(API_ENDPOINTS.AUTH.RESEND_OTP),
    RESET_PASSWORD: buildApiUrl(API_ENDPOINTS.AUTH.RESET_PASSWORD),
    VERIFY_EMAIL: buildApiUrl(API_ENDPOINTS.AUTH.VERIFY_EMAIL),
    RESEND_VERIFICATION: buildApiUrl(API_ENDPOINTS.AUTH.RESEND_VERIFICATION),
    SEND_NEW_EMAIL_VERIFICATION: buildApiUrl(API_ENDPOINTS.AUTH.SEND_NEW_EMAIL_VERIFICATION),
    VERIFY_NEW_EMAIL: buildApiUrl(API_ENDPOINTS.AUTH.VERIFY_NEW_EMAIL),
    SSO: {
      GOOGLE: buildApiUrl(API_ENDPOINTS.AUTH.SSO.GOOGLE),
      GOOGLE_CALLBACK: buildApiUrl(API_ENDPOINTS.AUTH.SSO.GOOGLE_CALLBACK),
      MICROSOFT: buildApiUrl(API_ENDPOINTS.AUTH.SSO.MICROSOFT),
      MICROSOFT_CALLBACK: buildApiUrl(API_ENDPOINTS.AUTH.SSO.MICROSOFT_CALLBACK),
      CONFIG: buildApiUrl(API_ENDPOINTS.AUTH.SSO.CONFIG),
    },
  },
  PROMPTS: {
    LIST: buildApiUrl(API_ENDPOINTS.PROMPTS.LIST),
    CREATE: buildApiUrl(API_ENDPOINTS.PROMPTS.CREATE),
    SEARCH: buildApiUrl(API_ENDPOINTS.PROMPTS.SEARCH),
    CATEGORY: (categoryName: string) => buildApiUrl(API_ENDPOINTS.PROMPTS.CATEGORY(categoryName)),
    DETAIL: (id: string) => buildApiUrl(API_ENDPOINTS.PROMPTS.DETAIL(id)),
    TOGGLE_VISIBILITY: (id: number) => buildApiUrl(API_ENDPOINTS.PROMPTS.TOGGLE_VISIBILITY(id)),
    VOTE: (id: string) => buildApiUrl(API_ENDPOINTS.PROMPTS.VOTE(id)),
    VERSIONS: {
      LIST: (promptId: string) => buildApiUrl(API_ENDPOINTS.PROMPTS.VERSIONS.LIST(promptId)),
      CREATE: (promptId: string) => buildApiUrl(API_ENDPOINTS.PROMPTS.VERSIONS.CREATE(promptId)),
    },
  },
  ANALYTICS: {
    PROMPT_EVENT: buildApiUrl(API_ENDPOINTS.ANALYTICS.PROMPT_EVENT),
    PROMPT_EVENT_DETAIL: (promptId: number) => buildApiUrl(API_ENDPOINTS.ANALYTICS.PROMPT_EVENT_DETAIL(promptId)),
  },
  COLLECTIONS: {
    LIST: buildApiUrl(API_ENDPOINTS.COLLECTIONS.LIST),
    CREATE: buildApiUrl(API_ENDPOINTS.COLLECTIONS.CREATE),
    DETAIL: (id: string) => buildApiUrl(API_ENDPOINTS.COLLECTIONS.DETAIL(id)),
    UPDATE: (id: string) => buildApiUrl(API_ENDPOINTS.COLLECTIONS.UPDATE(id)),
    DELETE: (id: string) => buildApiUrl(API_ENDPOINTS.COLLECTIONS.DELETE(id)),
    ADD_PROMPT: (id: string) => buildApiUrl(API_ENDPOINTS.COLLECTIONS.ADD_PROMPT(id)),
    REMOVE_PROMPT: (id: string, promptId: string) => buildApiUrl(API_ENDPOINTS.COLLECTIONS.REMOVE_PROMPT(id, promptId)),
    PROMPTS: (id: string) => buildApiUrl(API_ENDPOINTS.COLLECTIONS.PROMPTS(id)),
  },
  WAITLIST: {
    CREATE: buildApiUrl(API_ENDPOINTS.WAITLIST.CREATE),
  },
  USER_PROFILE: {
    UPDATE: buildApiUrl(API_ENDPOINTS.USER_PROFILE.UPDATE),
  },
  EMAIL_UPDATE: {
    SEND_VERIFICATION: buildApiUrl(API_ENDPOINTS.EMAIL_UPDATE.SEND_VERIFICATION),
    VERIFY: buildApiUrl(API_ENDPOINTS.EMAIL_UPDATE.VERIFY),
  },
  SUBSCRIPTION: {
    GET_USER_SUBSCRIPTION: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.GET_USER_SUBSCRIPTION),
    GET_USER_INVOICES: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.GET_USER_INVOICES),
    MANAGE: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.MANAGE),
    CANCEL: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.CANCEL),
    GET_PLANS: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.GET_PLANS),
    SYNC_CHECKOUT_SESSION: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.SYNC_CHECKOUT_SESSION),
    CHANGE_PLAN: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.CHANGE_PLAN),
    GET_SCHEDULED_CHANGE: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.GET_SCHEDULED_CHANGE),
    SCHEDULE_DOWNGRADE: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.SCHEDULE_DOWNGRADE),
    CANCEL_SCHEDULED_DOWNGRADE: buildApiUrl(API_ENDPOINTS.SUBSCRIPTION.CANCEL_SCHEDULED_DOWNGRADE),
  },
  DISCOUNTS: {
    VALIDATE: buildApiUrl(API_ENDPOINTS.DISCOUNTS.VALIDATE),
    APPLY_TO_SUBSCRIPTION: buildApiUrl(API_ENDPOINTS.DISCOUNTS.APPLY_TO_SUBSCRIPTION),
    USER_REDEMPTIONS: buildApiUrl(API_ENDPOINTS.DISCOUNTS.USER_REDEMPTIONS),
    CURRENT: buildApiUrl(API_ENDPOINTS.DISCOUNTS.CURRENT),
  },
  STRIPE: {
    CREATE_CHECKOUT_SESSION: buildApiUrl(API_ENDPOINTS.STRIPE.CREATE_CHECKOUT_SESSION),
    CREATE_PORTAL_SESSION: buildApiUrl(API_ENDPOINTS.STRIPE.CREATE_PORTAL_SESSION),
    GET_PRICES: buildApiUrl(API_ENDPOINTS.STRIPE.GET_PRICES),
  },
  AGENT: {
    ENHANCE: buildApiUrl(API_ENDPOINTS.AGENT.ENHANCE),
    HEALTH: buildApiUrl(API_ENDPOINTS.AGENT.HEALTH),
  },
  MODELS: {
    LIST: buildApiUrl(API_ENDPOINTS.MODELS.LIST),
  },
  USAGE: {
    LIMITS: buildApiUrl(API_ENDPOINTS.USAGE.LIMITS),
  },
  COMMENTS: (promptId: number) => `/api/prompts/${promptId}/comments`,
  VOTE_COMMENT: (commentId: number) => `/api/comments/${commentId}/vote`,
} as const;
